import { useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { updatePlayer } from '@/lib/api';
import { useApiErrorHandler } from '@/lib/useApiErrorHandler';
import type { Player } from '@swiss-magic/shared-types';

interface UseInlineEditParams {
  tournamentId: string | undefined;
  players: Player[];
  setPlayers: Dispatch<SetStateAction<Player[]>>;
}

export function useInlineEdit({
  tournamentId,
  players,
  setPlayers,
}: UseInlineEditParams) {
  const [editingCell, setEditingCell] = useState<{
    playerId: string;
    field: string;
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  const handleError = useApiErrorHandler();

  function startEdit(playerId: string, field: string, currentValue: string) {
    setEditingCell({ playerId, field });
    setEditValue(currentValue);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }

  async function commitEdit() {
    if (!editingCell || !tournamentId) return;
    const { playerId, field } = editingCell;
    const player = players.find((p) => p.id === playerId);
    if (!player) return;

    const fieldMap: Record<string, string> = {
      firstName: 'firstName',
      lastName: 'lastName',
      rating: 'rating',
      fideId: 'fideId',
      city: 'city',
      country: 'country',
      club: 'club',
    };

    const apiField = fieldMap[field];
    if (!apiField) return;

    const currentVal = String(player[field as keyof Player] ?? '');
    if (editValue === currentVal) {
      setEditingCell(null);
      return;
    }

    let value: string | number | null = editValue.trim();
    if (field === 'rating' || field === 'fideId') {
      value = value ? Number(value) : null;
    }
    if (value === '') value = null;

    try {
      const updated = await updatePlayer(tournamentId, playerId, {
        [apiField]: value,
      });
      setPlayers((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err: unknown) {
      handleError(err, 'Failed to save edit — try again');
    }
    setEditingCell(null);
  }

  function handleEditKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  }

  return {
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    editInputRef,
    startEdit,
    commitEdit,
    handleEditKeyDown,
  };
}
